/**
 * SegmentedControl — Pill-Row-Toggle (Sprint-16-Brand).
 *
 * Container: subtle card-alt Pill mit border-line/50.
 * Active: bg-navy text-white (matches Mockup 4b: 14T/30T/60T/90T-Toggle).
 * Inactive: text-ink-soft, hover-text-ink.
 *
 * Standard für 2-4-Way-Toggles: Zeit-Range, Filter-Modus, View-Switch.
 */
import { cn } from '@/shared/lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** `square` fuer Arbeitsflaechen mit eckiger Rasterkante; `pill` bleibt der Standard. */
  shape?: 'pill' | 'square';
  /** `touch` auf Flaechen, die auch am Telefon bedient werden: 44 px Trefferflaeche (§9). */
  size?: 'default' | 'touch';
  className?: string;
  'aria-label'?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  shape = 'pill',
  size = 'default',
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  const square = shape === 'square';
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'border-line/50 bg-card-alt inline-flex items-center gap-1 border p-1',
        square ? 'rounded-lg' : 'rounded-full',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'font-medium transition-colors',
              size === 'touch' && 'min-h-11',
              square ? 'px-5 py-2 text-[14px]' : 'px-4 py-1.5 text-[12px]',
              square ? 'rounded-md' : 'rounded-full',
              active
                ? 'bg-navy text-white'
                : square
                  ? 'text-ink hover:bg-card/70'
                  : 'text-ink-soft hover:text-ink',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
